import { useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'

import {
  SignOut,
  ArrowsLeftRight,
  Circle,
  X,
  MagnifyingGlass,
  MapPin,
  CheckCircle,
} from '@phosphor-icons/react'

import './Sidebar.scss'

import sidebarMenu from './menu'

import logo from '../../assets/images/logo.png'
import avatar from '../../assets/images/avatar.png'

// ─── Mock outlet data ─────────────────────────────────────────────────────────

const OUTLETS = [
  {
    id: 1,
    name: 'Sunway Outlet',
    address: 'Sunway Pyramid, Petaling Jaya, Selangor',
    status: 'open',
  },
  {
    id: 2,
    name: 'KLCC Outlet',
    address: 'Suria KLCC, Kuala Lumpur City Centre',
    status: 'open',
  },
  {
    id: 3,
    name: 'Mid Valley Outlet',
    address: 'Mid Valley Megamall, Kuala Lumpur',
    status: 'open',
  },
  {
    id: 4,
    name: 'IOI City Mall',
    address: 'IOI City Mall, Putrajaya',
    status: 'closed',
  },
  {
    id: 5,
    name: 'One Utama Outlet',
    address: '1 Utama Shopping Centre, Petaling Jaya',
    status: 'open',
  },
]

// ─── Outlet Switcher Modal (rendered via Portal) ──────────────────────────────

function OutletSwitcherModal({ currentOutlet, onSelect, onClose }) {
  const [search, setSearch] = useState('')

  const filtered = OUTLETS.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.address.toLowerCase().includes(search.toLowerCase())
  )

  return createPortal(
    <div className="outlet-modal-overlay" onClick={onClose}>
      <div className="outlet-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="outlet-modal__header">
          <div>
            <h2 className="outlet-modal__title">Switch outlet</h2>
            <p className="outlet-modal__subtitle">Select an outlet to manage</p>
          </div>
          <button className="outlet-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="outlet-modal__search">
          <MagnifyingGlass size={18} className="outlet-modal__search-icon" />
          <input
            type="text"
            placeholder="Search outlets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* List */}
        <div className="outlet-modal__list">
          {filtered.length === 0 && (
            <p className="outlet-modal__empty">No outlets found</p>
          )}

          {filtered.map((outlet) => {
            const isActive = outlet.id === currentOutlet.id
            const isClosed = outlet.status === 'closed'

            return (
              <button
                key={outlet.id}
                className={`outlet-modal__item ${isActive ? 'outlet-modal__item--active' : ''} ${isClosed ? 'outlet-modal__item--closed' : ''}`}
                onClick={() => {
                  if (isClosed) return
                  onSelect(outlet)
                  onClose()
                }}
                disabled={isClosed}
              >
                <div className="outlet-modal__item-icon">
                  <MapPin size={20} weight={isActive ? 'fill' : 'regular'} />
                </div>

                <div className="outlet-modal__item-info">
                  <p className="outlet-modal__item-name">{outlet.name}</p>
                  <p className="outlet-modal__item-address">{outlet.address}</p>
                </div>

                <div className="outlet-modal__item-right">
                  {isClosed ? (
                    <span className="outlet-modal__item-closed-badge">Closed</span>
                  ) : isActive ? (
                    <CheckCircle size={20} weight="fill" className="outlet-modal__item-check" />
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export default function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const [currentOutlet, setCurrentOutlet] = useState(OUTLETS[0])
  const [showOutletModal, setShowOutletModal] = useState(false)

  return (
    <>
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>

        {/* HEADER */}
        <div className="sidebar__header">
          <img src={logo} alt="Cuscapi" className="sidebar__logo" />
          <div className="sidebar__merchant">Hikari House</div>
        </div>

        {/* OUTLET */}
        <button
          className="sidebar__outlet"
          onClick={() => setShowOutletModal(true)}
        >
          <div className="sidebar__outlet-left">
            {currentOutlet.name}
          </div>
          <ArrowsLeftRight size={18} />
        </button>

        {/* NAV */}
        <div className="sidebar__nav">
          {sidebarMenu.map((group) => (
            <div className="sidebar__group" key={group.title}>
              <div className="sidebar__group-title">{group.title}</div>
              <div className="sidebar__menu">
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `sidebar__item ${isActive ? 'active' : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                      {item.badge && (
                        <div className="sidebar__badge">{item.badge}</div>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="sidebar__footer">
          <div className="sidebar__profile">
            <img src={avatar} alt="User" className="sidebar__avatar" />
            <div className="sidebar__name">Xavier Liew</div>
          </div>
          <div className="sidebar__logout">
            <SignOut size={18} />
            Logout
          </div>
        </div>
      </aside>

      {/* Portal renders directly into document.body — outside sidebar DOM */}
      {showOutletModal && (
        <OutletSwitcherModal
          currentOutlet={currentOutlet}
          onSelect={setCurrentOutlet}
          onClose={() => setShowOutletModal(false)}
        />
      )}
    </>
  )
}